import * as AT from '../../components/container/attributes';
import {HitUtils} from '@hit/components/src/utils/hit/HitUtils';
import Address from './Address';
import MultiLanguageDesignationModel from './MultiLanguageDesignationModel';
import ResourceCategory from './ResourceCategory';
import ResourceClassification from './ResourceClassification';
import Unit from './Unit';
import MultiLanguageField from './MultiLanguageField';
import BaseModel from './BaseModel';
import {HitOnlineEnums} from 'hit-online-web-ui/src/helpers/constants';
import {translate} from '../../plugins/i18n/i18n';
import {parseLocale} from '../../utils/language.js';

export default class Resource extends MultiLanguageDesignationModel {
  constructor({
    id,
    number,
    designationFrBe = undefined,
    designationDeBe = undefined,
    designationNlBe = undefined,
    designationFrLu = undefined,
    designationDeLu = undefined,
    designationFrFr = undefined,
    designationDeDe = undefined,
    designationNlNl = undefined,
    designationEnGb = undefined,
    designationEnIn = undefined,
    designationMrIn = undefined,
    designationHiIn = undefined,
    descriptionFrBe = undefined,
    descriptionDeBe = undefined,
    descriptionNlBe = undefined,
    descriptionFrLu = undefined,
    descriptionDeLu = undefined,
    descriptionFrFr = undefined,
    descriptionDeDe = undefined,
    descriptionNlNl = undefined,
    descriptionEnGb = undefined,
    descriptionEnIn = undefined,
    descriptionMrIn = undefined,
    descriptionHiIn = undefined,
    customisable = false,
    unit = undefined,
    resourceCategory = undefined,
    keywords = undefined,
    weight = undefined,
    volume = undefined,
    co2 = undefined,
    defaultSupplier = undefined,
    composite = undefined,
    unfold = undefined,
    purchasePrice = undefined,
    baseMarginApplied = false,
    baseMargin = undefined,
    basePrice = undefined,
    salesMarginApplied = false,
    salesMargin = undefined,
    salesPrice = undefined,
    active = false,
    memo = undefined,
    resourceType = undefined,
    titleLevel = undefined,
    weightUnit = undefined,
    volumeUnit = undefined,
    customData = undefined,
    scriptParam = undefined,

    // We also need to add the key how it is stored in the db (we use the db column names)
    no = undefined,
    designation = undefined,
    resourceClassification = undefined,
    pricePrecision = undefined,
  }) {
    super({
      id,
      number: number || no,
      designationFrBe,
      designationDeBe,
      designationNlBe,
      designationFrLu,
      designationDeLu,
      designationFrFr,
      designationDeDe,
      designationNlNl,
      designationEnGb,
      designationEnIn,
      designationMrIn,
      designationHiIn,
    });
    this.descriptions = new MultiLanguageField({
      descriptionFrBe,
      descriptionDeBe,
      descriptionNlBe,
      descriptionFrLu,
      descriptionDeLu,
      descriptionFrFr,
      descriptionDeDe,
      descriptionNlNl,
      descriptionEnGb,
      descriptionEnIn,
      descriptionMrIn,
      descriptionHiIn,
    });
    this.description = this.descriptions.value;
    this.customisable = customisable;
    this.unit = unit ? new Unit(unit) : null;
    this.resourceCategory = resourceCategory
      ? new ResourceCategory(resourceCategory)
      : null;
    this.keywords = keywords;
    this.weight = weight;
    this.volume = volume;
    this.co2 = co2;
    this.defaultSupplier = defaultSupplier
      ? new Address(defaultSupplier)
      : null;
    this.composite = composite;
    this.unfold = unfold;
    this.purchasePrice = purchasePrice;
    this.baseMarginApplied = baseMarginApplied;
    this.baseMargin = baseMargin;
    this.basePrice = basePrice;
    this.salesMarginApplied = salesMarginApplied;
    this.salesMargin = salesMargin;
    this.salesPrice = salesPrice;
    this.active = active;
    this.memo = memo;
    this.resourceType = resourceType
      ? HitOnlineEnums.getEnumValue('RESOURCE_TYPE', resourceType, translate)
      : null;
    this.titleLevel = titleLevel;
    this.weightUnit = weightUnit ? new Unit(weightUnit) : null;
    this.volumeUnit = volumeUnit ? new Unit(volumeUnit) : null;
    this.customData = customData;
    this.scriptParam = scriptParam;
    this.resourceClassification = resourceClassification
      ? new ResourceClassification(resourceClassification)
      : null;
    this.pricePrecision = pricePrecision;
    // For form items where we only store the designation in the data
    if (designation) {
      this.designation = designation;
    }
  }

  static fromApiRow(row) {
    if (!row) return null;
    if (row instanceof Resource) {
      return row;
    }
    const pickLocalized = (obj, prefix) => {
      if (!obj || typeof obj !== 'object') return undefined;
      if (obj[prefix]) return obj[prefix];
      const match = Object.keys(obj).find((key) => {
        const lower = key.toLowerCase();
        return (
          (lower === prefix ||
            lower.startsWith(`${prefix}_`) ||
            lower.startsWith(prefix)) &&
          obj[key]
        );
      });
      return match ? obj[match] : undefined;
    };
    const unitSource = row.unit;
    const unit = unitSource
      ? {
          ...unitSource,
          no: unitSource.no ?? unitSource.number,
          unit: unitSource.unit || pickLocalized(unitSource, 'unit'),
          designation:
            unitSource.designation || pickLocalized(unitSource, 'designation'),
        }
      : undefined;
    return new Resource({
      ...row,
      number: row.number || row.no,
      designation: row.designation || pickLocalized(row, 'designation'),
      unit,
    });
  }

  /**
   * Designation in the locale requested by the editor (customer language).
   * Without a requested locale, the connected user's designation is used.
   */
  get designationForDisplayLocale() {
    if (!this.displayLocale || !this.designations) return this.designation;
    try {
      return this.designations[parseLocale(this.displayLocale).idApp] || '';
    } catch {
      return this.designation;
    }
  }

  // We override the full designation so that the unit is also displayed if a resource is selected
  get fastModeBrowserDesignation() {
    const sd = HitUtils.createStandardDesignation({
      number: this.number,
      designation: this.designationForDisplayLocale,
    });
    const unitLabel =
      this.unit?.unitForLocale?.(this.displayLocale) ||
      this.unit?.unit ||
      this.unit?.designation ||
      '';
    if (!unitLabel) {
      return sd || '';
    }
    return sd ? `${sd} [${unitLabel}]` : `[${unitLabel}]`;
  }

  static get baseModuleForRedirection() {
    return 'resource-base';
  }
  get baseModuleForRedirection() {
    return Resource.baseModuleForRedirection;
  }

  static get defaultSort() {
    return {
      property: 'number',
      order: 'asc',
    };
  }

  getDefaultSort() {
    return Resource.defaultSort;
  }

  static get fkName() {
    return 'resource_id';
  }

  static get apiRoute() {
    return 'resource';
  }
  static get hasEntityStaffRolesBulkUpdate() {
    return true;
  }

  static get listPageRoute() {
    return 'resourceList';
  }

  static get detailPageRoute() {
    return 'resourceDetail';
  }

  get fkName() {
    return Resource.fkName;
  }

  get apiRoute() {
    return Resource.apiRoute;
  }

  get detailPageRoute() {
    return Resource.detailPageRoute;
  }

  get listPageRoute() {
    return Resource.listPageRoute;
  }

  static get attrsToStoreInForm() {
    return [
      'number',
      this.attributeCompanyLocale('designation'),
      this.attributeUserLocale('designation'),
      this.attributeCompanyLocale('description'),
      this.attributeUserLocale('description'),
      'basePrice',
      'salesPrice',
      'unit',
    ];
  }
  get attrsToStoreInForm() {
    return BaseModel.attrsToStoreInForm;
  }

  static get joinAttrsKey() {
    return [
      'number',
      this.attributeUserLocale('designation'),
      this.attributeCompanyLocale('designation'),
      this.attributeUserLocale('description'),
      this.attributeCompanyLocale('description'),
      'basePrice',
      'salesPrice',
      'unit',
      'customisable',
      'resourceType',
      'titleLevel',
      'resourceCategory',
      'salesMargin',
      'resourceClassification',
    ];
  }

  static get listDisplayAttrs() {
    return this.getAttributes([
      'titleLevel',
      'resourceType',
      'number',
      this.attributeUserLocale('designation'),
      this.attributeCompanyLocale('designation'),
      'unit',
      'resourceCategory',
      'purchasePrice',
      'salesPrice',
      'customisable',
      'composite',
      'active',
      'tags',
      'creationDate',
      'modificationDate',
      'scriptParam',
      'resourceClassification',
      'pricePrecision',
    ]);
  }

  static get browserDisplayAttrs() {
    return this.getAttributes(
      [
        'resourceType',
        'number',
        this.attributeUserLocale('designation'),
        this.attributeCompanyLocale('designation'),
        'unit',
        'resourceCategory',
        'basePrice',
        'salesPrice',
        'customisable',
        'composite',
        'tags',
        'resourceClassification',
      ],
      [
        this.attributeUserLocale('description'),
        this.attributeCompanyLocale('description'),
        'purchasePrice',
        'titleLevel',
        'baseMargin',
        'salesMargin',
        'scriptParam',
        'pricePrecision',
        'unfold',
      ],
    );
  }

  static get confidentialBrowserDisplayAttrs() {
    return this.getAttributes(
      [
        'resourceType',
        'number',
        this.attributeUserLocale('designation'),
        this.attributeCompanyLocale('designation'),
        'unit',
        'resourceCategory',
        'customisable',
        'composite',
        'tags',
        'resourceClassification',
      ],
      [
        this.attributeUserLocale('description'),
        this.attributeCompanyLocale('description'),
        'purchasePrice',
        'basePrice',
        'salesPrice',
        'titleLevel',
        'baseMargin',
        'salesMargin',
        'scriptParam',
        'pricePrecision',
        'unfold',
      ],
    );
  }

  static get createInitColumns() {
    return {
      resource_type: 'RESOURCE',
      base_margin_applied: true,
      sales_margin_applied: true,
      base_margin: 1.0,
      sales_margin: 1.0,
      purchase_price: 0.0,
      base_price: 0.0,
      sales_price: 0.0,
    };
  }

  static getIcon(resourceType, scriptParam) {
    switch (resourceType.id) {
      case 'RESOURCE':
        return 'box';
      case 'ITEM':
        if (scriptParam !== undefined && scriptParam !== null) {
          return 'wand_stars';
        }
        return 'build';
      default:
        return '';
    }
  }

  static get bulkUpdateAttrs() {
    return {
      'hit-base.bulk-update.general-information': this.getAttributes([
        'resourceType',
        'number',
        ...this.attributesAllCompanyLanguages('designation'),
        'customisable',
        'unit',
        'resourceCategory',
        'keywords',
        'active',
        'resourceClassification',
        'tags',
      ]),
      'hit-app.resource.packaging': this.getAttributes([
        'defaultSupplier',
        'weight',
        'weightUnit',
        'volume',
        'volumeUnit',
        'co2',
      ]),
      ...this.entityStaffRolesBulkUpdateAttrs,
    };
  }

  static get allAttributes() {
    return [
      new AT.EnumerationAttribute({
        name: 'resourceType',
        column: 'resource_type',
        enumType: 'RESOURCE_TYPE',
        enumClass: HitOnlineEnums,
        tableProperties: {
          header: 'hit-base.common.type',
          maxWidth: '0.5fr',
        },
      }),
      this.NUMBER_ATTRIBUTE({}, {maxWidth: '1fr'}),
      new AT.BooleanAttribute({
        name: 'customisable',
        default: false,
        tableProperties: {
          header: 'hit-app.resource.generic',
          sharable: true,
          maxWidth: '1fr',
        },
      }),
      new AT.FkUnitAttribute({
        name: 'unit',
        joinPath: ['unit!fk_resource_unit_id_unit'],
        tableProperties: {
          header: 'hit-base.common.unit',
        },
      }),
      new AT.FkResourceCategoryAttribute({
        name: 'resourceCategory',
        column: 'resource_category_id',
        joinPath: ['fk_resource_resource_category_id_resource_category'],
        tableProperties: {
          header: 'hit-base.common.resource-category',
        },
      }),
      new AT.FkResourceClassificationAttribute({
        name: 'resourceClassification',
        column: 'resource_classification_id',
        joinPath: [
          'fk_resource_resource_classification_id_resource_classification',
        ],
        tableProperties: {
          header: 'hit-base.common.resource-classification',
        },
      }),
      new AT.StringAttribute({
        name: 'keywords',
        column: 'keywords',
        tableProperties: {
          header: 'hit-app.resource.keywords',
          sharable: true,
          maxWidth: '0.55fr',
        },
      }),
      new AT.DecimalAttribute({
        name: 'weight',
        tableProperties: {
          header: 'hit-app.resource.weight',
          sharable: true,
          maxWidth: '0.5fr',
        },
      }),
      new AT.FkUnitAttribute({
        name: 'weightUnit',
        joinPath: ['unit!fk_resource_weight_unit_id_unit'],
        fkToUpdate: 'weight_unit_id',
        tableProperties: {
          header: 'hit-base.common.unit',
        },
        filters: {category: 'eq.mass'},
      }),
      new AT.DecimalAttribute({
        name: 'volume',
        tableProperties: {
          header: 'hit-app.resource.volume',
          sharable: true,
          maxWidth: '0.5fr',
        },
      }),
      new AT.DecimalAttribute({
        name: 'co2',
        tableProperties: {
          header: 'hit-app.resource.co2',
          sharable: true,
          maxWidth: '0.5fr',
        },
      }),
      new AT.FkUnitAttribute({
        name: 'volumeUnit',
        joinPath: ['unit!fk_resource_volume_unit_id_unit'],
        fkToUpdate: 'volume_unit_id',
        tableProperties: {
          header: 'hit-base.common.unit',
        },
        filters: {category: 'eq.volume'},
      }),
      new AT.FkAddressAttribute({
        name: 'defaultSupplier',
        fkToUpdate: 'default_supplier_id',
        joinPath: ['address!fk_resource_default_supplier_id_address'],
        column: 'default_supplier_id',
        tableProperties: {
          header: 'hit-app.common.default-supplier',
        },
        filters: {supplier: 'is.true'},
      }),
      new AT.BooleanAttribute({
        name: 'composite',
        tableProperties: {
          propertyName: 'composite',
          header: 'hit-app.resource.composite',
          maxWidth: '1fr',
        },
      }),
      new AT.BooleanAttribute({
        name: 'unfold',
        tableProperties: {
          header: 'hit-app.resource.unfold',
        },
      }),
      new AT.DecimalAttribute({
        name: 'purchasePrice',
        column: 'purchase_price',
        decimalPrecisionForcedByColumn: 'pricePrecision',
        creationValue: 0,
        tableProperties: {
          header: 'hit-app.common.purchase-price',
          maxWidth: '1fr',
        },
      }),
      new AT.BooleanAttribute({
        name: 'baseMarginApplied',
        column: 'base_margin_applied',
        tableProperties: {
          header: 'hit-app.common.base-margin',
        },
      }),
      new AT.DecimalAttribute({
        name: 'baseMargin',
        column: 'base_margin',
        decimalPrecision: 4,
        creationValue: 1,
        tableProperties: {
          header: 'hit-app.common.base-margin',
        },
      }),
      new AT.DecimalAttribute({
        name: 'basePrice',
        column: 'base_price',
        decimalPrecisionForcedByColumn: 'pricePrecision',
        creationValue: 0,
        tableProperties: {
          header: 'hit-app.common.base-price',
          maxWidth: '1fr',
        },
      }),
      new AT.BooleanAttribute({
        name: 'salesMarginApplied',
        column: 'sales_margin_applied',
        tableProperties: {
          header: 'hit-app.common.sales-margin',
        },
      }),
      new AT.DecimalAttribute({
        name: 'salesMargin',
        column: 'sales_margin',
        decimalPrecision: 4,
        creationValue: 1,
        tableProperties: {
          header: 'hit-app.common.sales-margin',
        },
      }),
      new AT.DecimalAttribute({
        name: 'salesPrice',
        column: 'sales_price',
        decimalPrecisionForcedByColumn: 'pricePrecision',
        creationValue: 0,
        tableProperties: {
          header: 'hit-app.common.sales-price-short',
          maxWidth: '1fr',
        },
      }),
      this.ACTIVE_ATTRIBUTE({}, {maxWidth: '1fr'}),
      this.TAGS_ATTRIBUTE({moduleUsage: 'resource', tableOrigin: 'resource'}),
      this.MEMO_RICH_TEXT_ATTRIBUTE(),
      new AT.IntegerAttribute({
        name: 'titleLevel',
        column: 'title_level',
      }),
      new AT.JsonObjectAttribute({
        name: 'customData',
        column: 'custom_data',
      }),
      new AT.JsonObjectAttribute({
        name: 'scriptParam',
        column: 'script_param',
        default: {},
      }),
      new AT.JsonObjectAttribute({
        name: 'scriptSource',
        column: 'script_source',
        default: '',
      }),
      new AT.DateAttribute({
        name: 'creationDate',
        column: 'creation_date',
        tableProperties: {
          header: 'hit-base.common.creation-date',
          hideByDefault: true,
        },
      }),
      new AT.DateAttribute({
        name: 'modificationDate',
        column: 'modification_date',
        tableProperties: {
          header: 'hit-base.common.last-modified',
          hideByDefault: true,
        },
      }),
      new AT.FkTaxCodeAttribute({
        name: 'invoiceTaxCode',
        joinPath: ['tax_code!fk_resource_invoice_tax_code_id_tax_code'],
        fkToUpdate: 'invoice_tax_code_id',
        tableProperties: {
          header: 'hit-app.common.invoice-tax-code',
        },
        filters: {
          or: ['transaction_type.eq.INVOICE', 'transaction_type.eq.BOTH'],
        },
      }),

      new AT.FkTaxCodeAttribute({
        name: 'creditNoteTaxCode',
        joinPath: ['tax_code!fk_resource_creditnote_tax_code_id_tax_code'],
        fkToUpdate: 'creditnote_tax_code_id',
        tableProperties: {
          header: 'hit-app.common.credit-note-tax-code',
        },
        filters: {
          or: ['transaction_type.eq.CREDITNOTE', 'transaction_type.eq.BOTH'],
        },
      }),
      new AT.IntegerAttribute({
        name: 'pricePrecision',
        column: 'price_precision',
      }),
      new AT.ArrayAttribute({
        name: 'stockQuantity',
        column: 'stock_resource(quantity)',
        forcedType: 'decimal',
        transform: (quantityArray) => {
          let sum = 0;
          for (const quantity of quantityArray ?? []) {
            sum += quantity.quantity;
          }
          return sum;
        },
        tableProperties: {
          header: 'hit-app.common.quantity',
          maxWidth: '1fr',
        },
      }),
      new AT.ArrayAttribute({
        name: 'minimumQuantity',
        column: 'stock_resource(stock_minimum_quantity)',
        forcedType: 'decimal',
        transform: (quantityArray) => {
          let sum = 0;
          for (const quantity of quantityArray ?? []) {
            sum += quantity.stock_minimum_quantity;
          }
          return sum;
        },
        tableProperties: {
          header: 'hit-app.common.minimum-quantity',
          maxWidth: '1fr',
        },
      }),
      new AT.ArrayAttribute({
        name: 'maximumQuantity',
        column: 'stock_resource(stock_maximum_quantity)',
        forcedType: 'decimal',
        transform: (quantityArray) => {
          let sum = 0;
          for (const quantity of quantityArray ?? []) {
            sum += quantity.stock_maximum_quantity;
          }
          return sum;
        },
        tableProperties: {
          header: 'hit-app.common.maximum-quantity',
          maxWidth: '1fr',
        },
      }),
    ]
      .concat(
        this.generateMultiLanguageField({
          key: 'designation',
          attributeClass: AT.StringAttribute,
          header: 'hit-base.common.designation',
        }),
      )
      .concat(
        this.generateMultiLanguageField({
          key: 'description',
          attributeClass: AT.RichTextAttribute,
          header: 'hit-base.common.description',
        }),
      );
  }
}
